// HST — Vue aérienne 3D évolutive du dépôt
// Moteur Three.js autonome (seule dépendance : `three`).
//
//   const engine = createDepotScene(container, { depot, tree, onSelect })
//   engine.update(depot, tree)   // à chaque changement d'état du jeu (construction, mission, RH…)
//   engine.select({ type, id })  // type : 'zone' | 'vehicle' | 'qg' | 'contract'
//   engine.rotate(±1) · engine.zoom(f) · engine.home() · engine.dispose()
//
// Le dépôt se construit à partir de `depot.built` (identifiants de l'arbre : P2…P14, EXT).
// Les véhicules sont modélisés d'après leur type/carrosserie, les marchandises d'après
// les contrats actifs, le personnel d'après les effectifs.

import * as THREE from 'three';
import { profileOf } from './depotProfiles';

// ───────────────────────── Implantation des zones ─────────────────────────
// x/z : centre au sol, w/d : emprise (m). L'axe +z pointe vers le portail.
export const LOTS = {
  P2: { x: 0, z: -52.5, w: 60, d: 61 },
  P3: { x: -11, z: -71, w: 34, d: 18 },
  P4: { x: -6, z: -60.5, w: 26, d: 3.5 },
  P6: { x: 11.5, z: -73, w: 11, d: 14 },
  P7: { x: -24, z: -59.5, w: 8, d: 5 },
  P8: { x: -21, z: -31, w: 16, d: 18 },
  P9: { x: -22, z: 8, w: 14, d: 12 },
  P10: { x: 19, z: 15.5, w: 8, d: 6 },
  P11: { x: 22, z: -42, w: 14, d: 16 },
  P12: { x: 23.5, z: -73, w: 11, d: 14 },
  P13: { x: 22.6, z: -4.6, w: 10, d: 10 },
  P14: { x: 26.5, z: 7, w: 6, d: 5 },
};
const DOCK_X = [-15, -9, -3, 3];
// Emplacements de stationnement (les 3 derniers arrivent avec l'agrandissement P2)
export const SLOTS = [
  { x: -11, z: 10 }, { x: 11, z: 6 }, { x: -11, z: -10 }, { x: 11, z: -14 },
  { x: -11, z: -36, req: 'P2' }, { x: 11, z: -30, req: 'P2' }, { x: 11, z: -58, req: 'P2' },
];

const PAL = {
  sky: '#cdd7e0', grass: '#b5ad7c', grassGreen: '#9aa86c', asphalt: '#6c7076', asphaltLt: '#767b81',
  line: '#ecebe4', gravel: '#a39b8a', concrete: '#c8c5be', white: '#e8e9eb', whiteWarm: '#efeee9',
  dark: '#4a4f57', roofDark: '#2a3346', red: '#c4473a', orange: '#e8762d', solar: '#243661',
  glass: '#7fa1c2', fence: '#9aa0a6', tree: '#5f7f3a', tree2: '#76893f', trunk: '#6d5440',
  tire: '#222428', chassis: '#2b2e33', yellow: '#e9b42a', hivis: '#f08a1c',
  navy: '#26344d', gold: '#e3a93b',
};
export const BRAND_COLORS = { VALREK: '#2f63d6', MANTOR: '#c93b2c', IVECTRA: '#1f8a6e', VOLVAX: '#1e2f4a', SCANEX: '#c48a1c', DAFORA: '#6d4cc4' };

const easeOutBack = (t) => 1 + 2.2 * Math.pow(t - 1, 3) + 1.2 * Math.pow(t - 1, 2);

export function createDepotScene(container, opts = {}) {
  let { depot = { built: [], vehicles: [], contracts: [], staff: {} }, tree = [], onSelect = () => {} } = opts;
  // 21/10/2026 — libellés d'interface dessinés dans la scène (badge zone
  // disponible) : fournis par le composant React selon la langue de l'UI.
  const UI = { available: '✦ DISPO', ...(opts.labels || {}) };
  // profil du site : environnement, langue des panneaux, sens de la disposition, concurrent racheté
  const prof = profileOf(depot);
  const T = prof.theme, LX = prof.lang, MX = prof.mirror ? -1 : 1, rival = prof.rival, acquiredSet = prof.acquired;
  const P = { ...PAL, sky: T.sky, grass: T.grass, grassGreen: T.grassGreen, tree: T.tree, tree2: T.tree2 };
  const HSTC = { navy: PAL.navy, gold: PAL.gold };

  // ───────────────────────── Rendu ─────────────────────────
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  const el = renderer.domElement;
  el.style.display = 'block';
  el.style.touchAction = 'none';
  container.appendChild(el);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(P.sky);
  scene.fog = new THREE.Fog(P.sky, T.fogNear || 230, T.fogFar || 520);
  scene.add(new THREE.HemisphereLight('#f4f7fb', T.hemiGround, 1.15));
  const sun = new THREE.DirectionalLight(T.sun, T.sunI);
  sun.castShadow = true;
  sun.shadow.mapSize.set(4096, 4096);
  Object.assign(sun.shadow.camera, { left: -120, right: 120, top: 120, bottom: -120, near: 1, far: 400 });
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.05;
  scene.add(sun, sun.target);
  // tout le décor vit dans `world`, inversé en x pour les dépôts en miroir
  const world = new THREE.Group();
  world.scale.x = MX;
  scene.add(world);

  // ───────────────────────── Caches ─────────────────────────
  const mats = new Map(), geos = new Map(), textures = [], texMats = new Map();
  const mat = (color, o = {}) => {
    const k = color + JSON.stringify(o);
    if (!mats.has(k)) mats.set(k, new THREE.MeshStandardMaterial({ color, roughness: 0.85, metalness: 0, ...o }));
    return mats.get(k);
  };
  const geo = (k, make) => { if (!geos.has(k)) geos.set(k, make()); return geos.get(k); };
  const gBox = (w, h, d) => geo(`b${w}|${h}|${d}`, () => new THREE.BoxGeometry(w, h, d));
  const gPlane = (w, d) => geo(`p${w}|${d}`, () => new THREE.PlaneGeometry(w, d).rotateX(-Math.PI / 2));
  const canvasTex = (w, h, draw, repeat) => {
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    draw(c.getContext('2d'), w, h);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8;
    if (repeat) { t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(repeat[0], repeat[1]); }
    textures.push(t);
    return t;
  };
  // textures contenant du texte : remises à l'endroit quand le site est en miroir
  const textTex = (w, h, draw) => { const t = canvasTex(w, h, draw); if (MX < 0) { t.wrapS = THREE.RepeatWrapping; t.repeat.x = -1; t.offset.x = 1; } return t; };
  const fit = (c, text, maxW, px, weight = 800) => { let s = px; do { c.font = `${weight} ${s}px Inter, Arial, sans-serif`; s -= 2; } while (c.measureText(text).width > maxW && s > 10); };
  const texMat = (key, make) => { if (!texMats.has(key)) texMats.set(key, make()); return texMats.get(key); };

  const box = (w, h, d, m, x = 0, y = 0, z = 0, parent, shadow = true) => {
    const mesh = new THREE.Mesh(gBox(w, h, d), m);
    mesh.position.set(x, y + h / 2, z);
    mesh.castShadow = shadow; mesh.receiveShadow = true;
    parent.add(mesh);
    return mesh;
  };
  const plane = (w, d, m, x, y, z, parent) => {
    const mesh = new THREE.Mesh(gPlane(w, d), typeof m === 'string' ? mat(m) : m);
    mesh.position.set(x, y, z); mesh.receiveShadow = true;
    parent.add(mesh);
    return mesh;
  };
  const cyl = (r, h, m, x, y, z, parent, seg = 16, axis = 'y') => {
    const g = geo(`c${r}|${h}|${seg}|${axis}`, () => {
      const c = new THREE.CylinderGeometry(r, r, h, seg);
      if (axis === 'x') c.rotateZ(Math.PI / 2);
      if (axis === 'z') c.rotateX(Math.PI / 2);
      return c;
    });
    const mesh = new THREE.Mesh(g, m);
    mesh.position.set(x, y, z); mesh.castShadow = true; mesh.receiveShadow = true;
    parent.add(mesh);
    return mesh;
  };

  const pickables = [];
  const tagPick = (o, pick) => { o.userData.pick = pick; pickables.push(o); return o; };
  const untagTree = (root) => {
    for (let i = pickables.length - 1; i >= 0; i--) {
      let o = pickables[i], inside = false;
      while (o) { if (o === root) { inside = true; break; } o = o.parent; }
      if (inside) pickables.splice(i, 1);
    }
  };

  // textures partagées
  const ribMat = (base, n) => texMat(`rib${base}${n}`, () => new THREE.MeshStandardMaterial({
    map: canvasTex(64, 64, (g, w, h) => {
      g.fillStyle = base; g.fillRect(0, 0, w, h);
      g.fillStyle = 'rgba(255,255,255,0.08)'; g.fillRect(0, 0, 8, h);
      g.fillStyle = 'rgba(0,0,0,0.12)'; g.fillRect(34, 0, 4, h);
    }, [n, 1]), roughness: 0.8,
  }));
  const solarMat = texMat('solar', () => new THREE.MeshStandardMaterial({
    map: canvasTex(128, 128, (g, w, h) => {
      g.fillStyle = P.solar; g.fillRect(0, 0, w, h);
      g.strokeStyle = 'rgba(200,215,240,0.55)'; g.lineWidth = 2;
      for (let i = 0; i <= w; i += 32) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i, h); g.stroke(); }
      for (let i = 0; i <= h; i += 16) { g.beginPath(); g.moveTo(0, i); g.lineTo(w, i); g.stroke(); }
    }, [3, 2]), roughness: 0.3, metalness: 0.35,
  }));
  const rollerMat = texMat('roller', () => new THREE.MeshStandardMaterial({
    map: canvasTex(64, 64, (g, w, h) => {
      g.fillStyle = '#b9bdc2'; g.fillRect(0, 0, w, h);
      g.fillStyle = 'rgba(0,0,0,0.18)'; for (let y = 0; y < h; y += 8) g.fillRect(0, y, w, 2);
    }, [1, 4]), roughness: 0.6,
  }));
  const hazardMat = texMat('hazard', () => new THREE.MeshStandardMaterial({
    map: canvasTex(64, 64, (g, w, h) => {
      g.fillStyle = '#f2c21b'; g.fillRect(0, 0, w, h);
      g.fillStyle = '#1d1d1f';
      for (let i = -h; i < w; i += 24) { g.beginPath(); g.moveTo(i, h); g.lineTo(i + 12, h); g.lineTo(i + 12 + h, 0); g.lineTo(i + h, 0); g.fill(); }
    }, [6, 1]), roughness: 0.7,
  }));
  const meshMat = texMat('mesh', () => new THREE.MeshStandardMaterial({
    map: canvasTex(64, 64, (g, w, h) => {
      g.clearRect(0, 0, w, h); g.strokeStyle = 'rgba(120,128,136,0.9)'; g.lineWidth = 2;
      for (let i = -h; i < w; i += 10) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i + h, h); g.stroke(); g.beginPath(); g.moveTo(i + h, 0); g.lineTo(i, h); g.stroke(); }
    }, [12, 1]), transparent: true, side: THREE.DoubleSide, roughness: 0.9, alphaTest: 0.2,
  }));
  const glassMat = mat(P.glass, { roughness: 0.25, metalness: 0.2 });

  // ───────────────────────── Environnement fixe ─────────────────────────
  const env = new THREE.Group();
  world.add(env);
  plane(1400, 1400, P.grass, 0, 0, 0, env);
  plane(1400, 9, P.asphalt, 0, 0.01, 33, env);
  plane(1400, 1.5, P.concrete, 0, 0.015, 27.8, env);
  plane(14, 6, P.asphalt, 0, 0.02, 26.5, env);
  {
    const dash = new THREE.InstancedMesh(gBox(3, 0.02, 0.22), mat(P.line), 140);
    const m4 = new THREE.Matrix4();
    for (let i = 0; i < 140; i++) { m4.makeTranslation(-420 + i * 6, 0.03, 33); dash.setMatrixAt(i, m4); }
    env.add(dash);
  }
  // ── décor propre à l'environnement du dépôt ──
  const noPlains = T.decor === 'port';
  if (!noPlains) {
    plane(22, 1400, P.asphalt, 92, 0.01, 0, env);
    box(0.6, 0.8, 1400, mat(P.concrete), 92, 0, 0, env, false);
    for (const x of [86, 98]) {
      const dash = new THREE.InstancedMesh(gBox(0.2, 0.02, 4), mat(P.line), 120);
      const m4 = new THREE.Matrix4();
      for (let i = 0; i < 120; i++) { m4.makeTranslation(x, 0.03, -420 + i * 8); dash.setMatrixAt(i, m4); }
      env.add(dash);
    }
  }
  const block = (x, z, w, h, d, c, roof = '#9fa3a8') => {
    plane(w + 20, d + 20, P.concrete, x, 0.008, z, env);
    box(w, h, d, mat(c), x, 0, z, env);
    box(w + 0.4, 0.4, d + 0.4, mat(roof), x, h, z, env, false);
  };
  const windowsMat = (base) => texMat(`win${base}`, () => new THREE.MeshStandardMaterial({
    map: canvasTex(64, 64, (c, w, h) => { c.fillStyle = base; c.fillRect(0, 0, w, h); c.fillStyle = 'rgba(70,95,125,0.75)'; for (let y = 6; y < h; y += 16) for (let x = 4; x < w; x += 16) c.fillRect(x, y, 10, 8); }, [6, 4]), roughness: 0.7,
  }));
  const plantTree = (x, z, s, parent) => {
    const g = new THREE.Group(); g.position.set(x, 0, z); g.scale.setScalar(s); parent.add(g);
    const alt = ((Math.round(x) * 7 + Math.round(z) * 3) & 1) === 1;
    const crownMat = mat(alt ? P.tree : P.tree2, { flatShading: true });
    if (T.trees === 'sapins') {
      cyl(0.25, 1.6, mat(P.trunk), 0, 0.8, 0, g, 6);
      [[2.6, 3.4, 2.6], [2.0, 3.0, 4.4], [1.3, 2.6, 6.0]].forEach(([r, h, y], i) => {
        const c = new THREE.Mesh(geo(`sap${i}`, () => new THREE.ConeGeometry(r, h, 8)), crownMat); c.position.y = y; c.castShadow = true; g.add(c);
      });
      return;
    }
    if (T.trees === 'pins') {
      if (alt) { // cyprès
        const c = new THREE.Mesh(geo('cypres', () => new THREE.ConeGeometry(1.1, 8, 8)), mat('#3b5a2c', { flatShading: true })); c.position.y = 4.2; c.castShadow = true; g.add(c);
      } else { // pin parasol
        cyl(0.3, 4.2, mat(P.trunk), 0, 2.1, 0, g, 6);
        const c = new THREE.Mesh(geo('parasol', () => new THREE.SphereGeometry(3, 10, 6)), crownMat); c.scale.set(1, 0.42, 1); c.position.y = 4.9; c.castShadow = true; g.add(c);
      }
      return;
    }
    cyl(0.25, 2.2, mat(P.trunk), 0, 1.1, 0, g, 6);
    const c = new THREE.Mesh(geo('crown', () => new THREE.IcosahedronGeometry(2.2, 1)), crownMat);
    c.position.y = 3.8; c.scale.set(1, 1.15, 1); c.castShadow = true; g.add(c);
  };
  const treeRows = (dense) => {
    for (let z = -110; z <= 20; z += dense ? 13 : 26) { plantTree(-68 + ((z * 5) % 4), z, 1 + Math.abs((z * 3) % 3) * 0.12, env); if (!noPlains) plantTree(40 + ((z * 7) % 5), z + 4, 1.1, env); }
    for (let x = -60; x <= 60; x += dense ? 15 : 30) plantTree(x + 3, 46 + ((x * 3) % 4), 1, env);
  };
  if (T.decor === 'industrie') {
    [[-130, -60, 50, 9, 70, '#c6c8cb'], [-100, 45, 40, 7, 30, '#b9bcc0'], [-40, -160, 80, 10, 40, '#cfd0d2'], [45, -160, 60, 8, 36, '#a9adb3'], [140, -90, 50, 9, 60, '#c4c6c9'], [150, 20, 40, 6, 40, '#babdc1'], [-30, 95, 60, 8, 30, '#c9cbce']].forEach((b) => block(...b));
    treeRows(true);
  } else if (T.decor === 'collines') {
    // collines et maisons aux toits de tuiles
    [[-250, -150, 140, 42], [-260, 60, 120, 34], [280, -180, 150, 46], [290, 40, 120, 32], [0, -300, 220, 70]].forEach(([x, z, r, h]) => {
      const m = new THREE.Mesh(geo(`hill${r}`, () => new THREE.SphereGeometry(1, 20, 10, 0, Math.PI * 2, 0, Math.PI / 2)), mat('#b9a978', { flatShading: true }));
      m.scale.set(r, h, r * 0.7); m.position.set(x, 0, z); m.receiveShadow = true; env.add(m);
    });
    const house = (x, z, rot) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = rot; env.add(g);
      box(8, 4.5, 6, mat('#efe4cf'), 0, 0, 0, g);
      const roof = new THREE.Mesh(geo('tuile', () => new THREE.ConeGeometry(6.2, 2.4, 4).rotateY(Math.PI / 4)), mat('#c0603b', { flatShading: true }));
      roof.scale.set(1, 1, 0.75); roof.position.y = 5.7; roof.castShadow = true; g.add(roof);
    };
    [[-110, 60, 0.2], [-130, 20, 1], [-95, -40, 0.4], [130, 55, 0.7], [150, -20, 0.1], [-60, 80, 1.2], [70, 85, 0.5], [-150, -110, 0.9], [160, -120, 0.3]].forEach((h) => house(...h));
    block(-130, -150, 50, 8, 40, '#e3dccb', '#c9bfa6');
    treeRows(true);
  } else if (T.decor === 'montagnes') {
    [[-250, -30, 150, 150], [-235, -190, 140, 170], [-260, 130, 140, 120], [285, -60, 165, 175], [270, -230, 150, 190], [300, 110, 150, 130], [0, -330, 190, 230]].forEach(([x, z, r, h]) => {
      const m = new THREE.Mesh(geo(`mt${r}${h}`, () => new THREE.ConeGeometry(r, h, 7)), mat('#7b8a99', { flatShading: true }));
      m.position.set(x, h / 2 - 2, z); env.add(m);
      const s = new THREE.Mesh(geo(`snow${r}${h}`, () => new THREE.ConeGeometry(r * 0.32, h * 0.32, 7)), mat('#f4f7fa', { flatShading: true }));
      s.position.set(x, h - h * 0.16 - 2, z); env.add(s);
    });
    block(-120, -60, 40, 8, 50, '#c9c4b8');
    block(140, 30, 36, 7, 30, '#bdbab1');
    treeRows(true);
    for (let i = 0; i < 26; i++) plantTree(-200 + ((i * 53) % 400), -130 - ((i * 37) % 80), 1.2, env);
  } else if (T.decor === 'port') {
    // bassin, quai, portiques et piles de conteneurs
    const water = new THREE.Mesh(gPlane(1400, 600), mat('#5d7a8f', { roughness: 0.25, metalness: 0.2 }));
    water.position.set(0, 0.05, -420); env.add(water);
    box(1400, 1.2, 4, mat('#a8a49a'), 0, -0.6, -122, env, false);
    const cont = ['#c4473a', '#2f63d6', '#1f8a6e', '#e3a93b', '#6d4cc4', '#8d949b', '#d77a2f'];
    for (let i = 0; i < 70; i++) {
      const x = -230 + (i % 14) * 7.2, z = -98 + Math.floor(i / 14) * -3.2, h = 1 + ((i * 7) % 3);
      for (let k = 0; k < h; k++) box(6.1, 2.6, 2.5, mat(cont[(i + k) % cont.length]), x, k * 2.6, z, env);
    }
    const crane = (x) => {
      const g = new THREE.Group(); g.position.set(x, 0, -112); env.add(g);
      const red = mat('#c94a33', { roughness: 0.5 });
      for (const [dx, dz] of [[-7, -5], [7, -5], [-7, 5], [7, 5]]) box(1, 30, 1, red, dx, 0, dz, g);
      box(16, 2, 1.2, red, 0, 30, -5, g); box(16, 2, 1.2, red, 0, 30, 5, g);
      box(2, 2, 62, red, 0, 32, -22, g);
      box(4, 3, 4, mat('#e9ecef'), 0, 27, -8, g);
    };
    [60, 110, 160].forEach(crane);
    const ship = new THREE.Group(); ship.position.set(110, 0, -150); env.add(ship);
    box(150, 9, 28, mat('#2b3442'), 0, -4, 0, ship); box(150, 1, 28.2, mat('#a63d33'), 0, -4, 0, ship, false);
    for (let i = 0; i < 40; i++) box(6.1, 2.6, 2.5, mat(cont[i % cont.length]), -60 + (i % 10) * 12, 5 + Math.floor(i / 20) * 2.6, -9 + (Math.floor(i / 10) % 2) * 18, ship);
    box(14, 14, 24, mat('#eceef0'), 62, 5, 0, ship);
    block(150, 10, 50, 10, 60, '#c4c6c9');
    block(-130, 40, 40, 8, 30, '#b9bcc0');
    treeRows(false);
  } else if (T.decor === 'ville') {
    const cols = ['#d8d3c8', '#c9ccd0', '#e3ddd2', '#b9bec4', '#d2c4b2'];
    const spots = [[-110, -60], [-110, -10], [-110, 40], [-160, -60], [-160, -10], [-160, 40], [-60, -150], [0, -150], [60, -150], [140, -80], [140, -20], [140, 40], [190, -80], [190, -20], [-40, 90], [30, 90], [100, 90], [-110, 90]];
    spots.forEach(([x, z], i) => {
      const h = 14 + ((i * 11) % 26), w = 30 + ((i * 7) % 12), d = 30 + ((i * 5) % 10);
      plane(w + 10, d + 10, P.concrete, x, 0.008, z, env);
      const wm = windowsMat(cols[i % cols.length]);
      const m = new THREE.Mesh(gBox(w, h, d), [wm, wm, mat('#8f949a'), mat('#8f949a'), wm, wm]);
      m.position.set(x, h / 2, z); m.castShadow = m.receiveShadow = true; env.add(m);
    });
    for (let x = -66; x <= 66; x += 11) plantTree(x, 40.6, 0.9, env);
  }
  const traffic = [];
  const makeCar = (col) => {
    const g = new THREE.Group();
    box(1.9, 0.8, 4.3, mat(col, { roughness: 0.4 }), 0, 0.3, 0, g);
    box(1.7, 0.6, 2.2, mat(col, { roughness: 0.4 }), 0, 1.1, -0.2, g);
    box(1.72, 0.45, 2.0, glassMat, 0, 1.15, -0.2, g, false);
    return g;
  };
  if (!noPlains) ['#d9dde3', '#2c3440', '#8a1f22', '#4f6f9a', '#e8e8e8', '#5c6066', '#c7ccd2'].forEach((c, i) => {
    const v = { obj: makeCar(c), x: i % 2 ? 87.5 : 96.5, z: -300 + i * 95, dir: i % 2 ? 1 : -1, speed: 26 + i * 3 };
    v.obj.rotation.y = v.dir > 0 ? 0 : Math.PI;
    env.add(v.obj); traffic.push(v);
  });
  {
    const v = { obj: makeCar('#e5e7ea'), x: -200, z: 31, speed: 14, road: true };
    v.obj.rotation.y = Math.PI / 2; env.add(v.obj); traffic.push(v);
  }

  // ───────────────────────── Site (reconstruit à chaque évolution) ─────────────────────────
  let site = new THREE.Group();
  world.add(site);
  let prevBuilt = null;
  const growing = [], dusts = [], badges = [];
  let walkers = [], forklifts = [];

  // Une zone construite implique ses prérequis (sauvegardes anciennes : quai ou frigo
  // hérités sans entrepôt) — sinon des quais flotteraient sans bâtiment.
  const builtSet = () => {
    const b = new Set(['P1', ...(depot.built || [])]);
    const stack = [...b];
    while (stack.length) {
      const id = stack.pop();
      for (const r of nodeById(id)?.requires || []) if (!b.has(r)) { b.add(r); stack.push(r); }
    }
    return b;
  };
  const nodeById = (id) => tree.find((n) => n.id === id);
  const isAvailable = (id, b) => {
    const n = nodeById(id);
    if (!n || b.has(id)) return false;
    if (Array.isArray(depot.available)) return depot.available.includes(id);
    return (n.requires || []).every((r) => b.has(r));
  };

  const badgeMat = (text, bg, fg) => texMat(`badge${text}${bg}`, () => new THREE.SpriteMaterial({
    map: canvasTex(256, 112, (g, w) => {
      g.fillStyle = bg;
      g.beginPath(); g.roundRect(8, 8, w - 16, 60, 30); g.fill();
      g.beginPath(); g.moveTo(w / 2 - 12, 66); g.lineTo(w / 2, 92); g.lineTo(w / 2 + 12, 66); g.fill();
      g.fillStyle = fg; g.font = '800 34px Inter, Arial, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillText(text, w / 2, 39);
    }), depthTest: false, transparent: true,
  }));
  const addBadge = (parent, x, y, z, text, bg, fg, pick) => {
    const s = new THREE.Sprite(badgeMat(text, bg, fg));
    s.center.set(0.5, 0.1);
    s.position.set(x, y, z);
    s.renderOrder = 30;
    s.userData.baseY = y; s.userData.phase = Math.random() * 6;
    tagPick(s, pick);
    parent.add(s); badges.push(s);
  };

  const lotOutline = (lot, color, parent, opacity) => {
    const g = new THREE.Group();
    const m = new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false });
    const fill = new THREE.Mesh(gPlane(lot.w, lot.d), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: opacity * 0.16, depthWrite: false }));
    fill.position.set(lot.x, 0.06, lot.z); g.add(fill);
    const x0 = lot.x - lot.w / 2, x1 = lot.x + lot.w / 2, z0 = lot.z - lot.d / 2, z1 = lot.z + lot.d / 2;
    const dashes = (ax, az, bx, bz) => {
      const len = Math.hypot(bx - ax, bz - az), n = Math.max(2, Math.floor(len / 1.6));
      for (let i = 0; i < n; i += 2) {
        const t = (i + 0.5) / n;
        const seg = new THREE.Mesh(gBox(1, 0.03, 1), m);
        seg.scale.set(ax !== bx ? len / n : 0.32, 1, az !== bz ? len / n : 0.32);
        seg.position.set(ax + (bx - ax) * t, 0.07, az + (bz - az) * t);
        g.add(seg);
      }
    };
    dashes(x0, z0, x1, z0); dashes(x0, z1, x1, z1); dashes(x0, z0, x0, z1); dashes(x1, z0, x1, z1);
    parent.add(g);
  };

  // clôture le long d'un segment axe x ou axe z, avec éventuelle ouverture [a, b]
  const fence = (ax, az, bx, bz, parent, gap) => {
    const ranges = gap ? [[ax, gap[0]], [gap[1], bx]] : [[ax, bx]];
    const posts = [];
    ranges.forEach(([ra, rb]) => {
      const horiz = az === bz;
      const a = horiz ? ra : az, b = horiz ? rb : bz;
      const len = Math.abs(b - a); if (len < 0.3) return;
      const n = Math.max(1, Math.round(len / 3.5));
      for (let k = 0; k <= n; k++) posts.push(horiz ? [a + (b - a) * k / n, az] : [ax, a + (b - a) * k / n]);
      const p = new THREE.Mesh(gBox(horiz ? len : 0.02, 2, horiz ? 0.02 : len), meshMat);
      p.position.set(horiz ? (a + b) / 2 : ax, 1.15, horiz ? az : (a + b) / 2);
      parent.add(p);
      box(horiz ? len : 0.06, 0.06, horiz ? 0.06 : len, mat(P.fence), horiz ? (a + b) / 2 : ax, 2.15, horiz ? az : (a + b) / 2, parent, false);
    });
    const pm = new THREE.InstancedMesh(gBox(0.12, 2.3, 0.12), mat(P.fence), posts.length);
    const m4 = new THREE.Matrix4();
    posts.forEach(([x, z], i) => { m4.makeTranslation(x, 1.15, z); pm.setMatrixAt(i, m4); });
    pm.castShadow = true;
    parent.add(pm);
  };

  const person = (x, z, parent, vest = P.hivis) => {
    const g = new THREE.Group(); g.position.set(x, 0, z); parent.add(g);
    const body = new THREE.Mesh(geo('capsule', () => new THREE.CapsuleGeometry(0.28, 0.8, 4, 8)), mat(vest));
    body.position.y = 0.95; body.castShadow = true; g.add(body);
    const head = new THREE.Mesh(geo('head', () => new THREE.SphereGeometry(0.2, 10, 8)), mat('#e2b48e'));
    head.position.y = 1.72; g.add(head);
    g.rotation.y = Math.random() * 6;
    g.userData.bob = Math.random() * 6;
    walkers.push(g);
    return g;
  };

  const makeForklift = (parent) => {
    const g = new THREE.Group();
    box(1.3, 0.85, 2.0, mat(P.yellow, { roughness: 0.5 }), 0, 0.35, 0, g);
    box(1.32, 0.8, 0.5, mat(P.chassis), 0, 0.35, -1.0, g);
    for (const [x, z] of [[-0.62, 0.65], [0.62, 0.65], [-0.62, -0.7], [0.62, -0.7]]) cyl(0.3, 0.3, mat(P.tire), x, 0.3, z, g, 12, 'x');
    for (const [x, z] of [[-0.58, 0.55], [0.58, 0.55], [-0.58, -0.75], [0.58, -0.75]]) box(0.08, 1.65, 0.08, mat(P.chassis), x, 1.2, z, g);
    box(1.3, 0.08, 1.45, mat(P.chassis), 0, 2.85, -0.1, g);
    const head = new THREE.Mesh(geo('head', () => new THREE.SphereGeometry(0.2, 10, 8)), mat('#e2b48e'));
    head.position.set(0, 2.05, -0.25); g.add(head);
    box(0.5, 0.6, 0.4, mat(P.hivis), 0, 1.2, -0.25, g);
    box(0.12, 3.1, 0.12, mat(P.chassis), -0.45, 0.2, 1.12, g);
    box(0.12, 3.1, 0.12, mat(P.chassis), 0.45, 0.2, 1.12, g);
    const carriage = new THREE.Group(); carriage.position.set(0, 0.15, 1.22); g.add(carriage);
    box(1.05, 0.6, 0.08, mat(P.chassis), 0, 0, 0, carriage);
    box(0.14, 0.06, 1.3, mat(P.chassis), -0.3, 0, 0.65, carriage);
    box(0.14, 0.06, 1.3, mat(P.chassis), 0.3, 0, 0.65, carriage);
    const load = new THREE.Group(); load.position.set(0, 0.06, 0.7); carriage.add(load);
    box(1.2, 0.16, 1.2, mat('#b89566'), 0, 0, 0, load);
    box(1.05, 0.75, 1.05, mat('#c9995e'), 0, 0.16, 0, load);
    g.userData = { carriage, load };
    parent.add(g);
    return g;
  };

  // ── marchandises visibles selon les contrats actifs ──
  const goods = {
    sec: (x, z, n, parent) => {
      for (let i = 0; i < n; i++) {
        const gx = x + Math.floor(i / 2) * 2.1, gz = z + (i % 2) * 2.3;
        box(1.8, 0.18, 1.5, mat('#b89566'), gx, 0, gz, parent);
        box(1.6, 0.9 + (i % 3) * 0.4, 1.3, mat(i % 3 ? '#c9995e' : '#b8864f'), gx, 0.18, gz, parent);
      }
    },
    frigo: (x, z, n, parent) => {
      for (let i = 0; i < n; i++) {
        box(1.6, 1.7, 1.3, mat('#e9eef3'), x + (i % 3) * 2, 0, z - Math.floor(i / 3) * 2, parent);
        box(1.62, 0.25, 1.32, mat('#3a7bd5'), x + (i % 3) * 2, 1.2, z - Math.floor(i / 3) * 2, parent, false);
      }
    },
    btp: (x, z, n, parent) => {
      for (let i = 0; i < n; i++) {
        if (i % 2 === 0) for (let k = 0; k < 6; k++) cyl(0.12, 6, mat('#7a4f35'), x + (k % 3) * 0.26 + i * 0.6, 0.15 + Math.floor(k / 3) * 0.22, z, parent, 6, 'z');
        else { box(1.2, 1.2, 1.2, mat('#e7e1cf'), x + i * 1.4, 0, z, parent); box(0.2, 0.25, 0.2, mat('#d9cfb5'), x + i * 1.4, 1.2, z, parent, false); }
      }
    },
    adr: (x, z, n, parent) => {
      const cols = ['#e6c21a', '#e6c21a', '#d9541f', '#e6c21a'];
      for (let i = 0; i < n; i++) {
        const gx = x + (i % 3) * 2.6, gz = z - Math.floor(i / 3) * 2.8;
        box(2.1, 0.15, 2.1, mat('#5b6068'), gx, 0, gz, parent);
        for (let k = 0; k < 4; k++) cyl(0.42, 1.0, mat(cols[(i + k) % 4], { roughness: 0.5 }), gx - 0.5 + (k % 2), 0.65, gz - 0.5 + Math.floor(k / 2), parent, 14);
      }
    },
    vrac: (x, z, n, parent) => {
      for (let i = 0; i < n; i++) {
        if (i > 2) break;
        const c = new THREE.Mesh(geo('cone', () => new THREE.ConeGeometry(2.1, 1.8, 10)), mat('#b9a27c', { flatShading: true }));
        c.position.set(x + i * 4.5, 0.9, z); c.castShadow = true; parent.add(c);
      }
    },
  };
  // emplacement réel selon les zones construites (null = pas de stockage possible)
  const goodsSpot = (kind, b) => {
    if (kind === 'frigo') return b.has('P6') ? [14.6, -62.6] : null;
    if (kind === 'adr') return b.has('P12') ? [20.6, -69] : null;
    if (kind === 'btp') return b.has('P7') ? [-28.4, -47.5] : [-27, 22];
    if (kind === 'sec') return b.has('P3') ? [-17.2, -57.6] : [-23, 19.6];
    if (kind === 'vrac') return b.has('P2') ? [-17.5, -51] : null;
    return null;
  };

  function laneMarks(g, z0, z1) {
    const n = Math.floor(Math.abs(z0 - z1) / 5);
    for (let i = 0; i < n; i++) for (const x of [-3, 3]) box(0.2, 0.02, 2.4, mat(P.line), x, 0.03, z0 - 2.5 - i * 5, g, false);
  }
  function slotMark(s, g) {
    const long = 18, wide = 3.8;
    for (const sd of [-1, 1]) {
      if (s.rot) box(long, 0.02, 0.18, mat(P.line), s.x, 0.03, s.z + sd * wide / 2, g, false);
      else box(0.18, 0.02, long, mat(P.line), s.x + sd * wide / 2, 0.03, s.z, g, false);
    }
  }
  function streetLamp(x, z, g) {
    cyl(0.12, 9, mat('#8d949b', { metalness: 0.4 }), x, 4.5, z, g, 8);
    box(1.6, 0.2, 0.5, mat('#5c6168'), x + (x < 0 ? 0.7 : -0.7), 9, z, g, false);
  }

  // ── bâtiments de l'arbre de développement ──
  // Flux du site : portail → contrôle → QG et carburant à l'entrée → stationnement le long de l'allée
  // → atelier et hangar à mi-parcours → entrepôt à quais au fond, zone ADR isolée dans l'angle.
  const gold = () => mat(P.gold, { roughness: 0.45, metalness: 0.2 });
  const gable = (key, w, rise, depth) => geo(key, () => {
    const s = new THREE.Shape(); s.moveTo(-w / 2, 0); s.lineTo(0, rise); s.lineTo(w / 2, 0); s.lineTo(-w / 2, 0);
    return new THREE.ExtrudeGeometry(s, { depth, bevelEnabled: false }).translate(0, 0, -depth / 2);
  });
  const builders = {
    P1(g, b) { // site de base : cour, portail, poste de garde, QG
      plane(60, 46, P.asphalt, 0, 0.02, 1, g);
      laneMarks(g, 24, -20);
      const gm = mat('#8d949b', { roughness: 0.6, metalness: 0.4 });
      box(0.3, 2.6, 0.3, gm, -6.2, 0, 24, g); box(0.3, 2.6, 0.3, gm, 6.2, 0, 24, g);
      [-1, 1].forEach((s) => {
        const lg = new THREE.Group(); lg.position.set(s * 6.2, 0, 24); lg.rotation.y = s * 1.05; g.add(lg);
        box(5.8, 0.1, 0.1, gm, -s * 2.9, 2.2, 0, lg); box(5.8, 0.1, 0.1, gm, -s * 2.9, 0.2, 0, lg);
        box(0.1, 2.1, 0.1, gm, -s * 5.8, 0.2, 0, lg);
        const p = new THREE.Mesh(gBox(5.8, 2, 0.02), meshMat); p.position.set(-s * 2.9, 1.2, 0); lg.add(p);
      });
      // poste de contrôle
      box(3, 2.9, 3.2, mat(P.white), 9, 0, 21.6, g);
      box(3.4, 0.3, 3.6, mat(P.navy), 9, 2.9, 21.6, g);
      box(3.02, 1, 2.2, glassMat, 9, 1.4, 21.6, g, false);
      // QG
      const qg = new THREE.Group(); g.add(qg);
      tagPick(qg, { type: 'qg', id: 'QG' });
      box(12, 4, 12, mat(P.white), 22, 0, -4, qg);
      box(10, 3.4, 10, mat(P.white), 22.6, 4, -4.6, qg);
      box(12.1, 0.5, 12.1, mat(P.navy), 22, 3.7, -4, qg);
      box(0.06, 1.4, 10, glassMat, 15.97, 1.3, -4, qg, false);
      box(10.4, 1.4, 0.06, glassMat, 22, 1.3, 2.03, qg, false);
      box(0.06, 1.4, 8.4, glassMat, 17.57, 4.9, -4.6, qg, false);
      box(8.4, 1.4, 0.06, glassMat, 22.6, 4.9, 0.43, qg, false);
      box(10.4, 0.3, 10.4, mat('#d6d9dd'), 22.6, 7.4, -4.6, qg);
      box(10.5, 0.18, 10.5, gold(), 22.6, 7.25, -4.6, qg, false);
      box(2.6, 1.2, 2, mat('#b4b9bf'), 25.5, 7.7, -8, qg);
      box(3.6, 0.25, 1.8, mat(P.navy), 15.4, 2.9, -2, qg);
      for (const [x, z] of [[-29, 22], [29, 22], [-29, -19], [29, -19]]) streetLamp(x, z, g);
      SLOTS.filter((s) => !s.req).forEach((s) => slotMark(s, g));
      const back = b.has('P2') ? -83 : -22;
      // hangar de base du dépôt (zone « garage » de départ, présente sur tous les sites)
      {
        const hx = -21, hz = -9, hw = 14, hd = 17, hh = 6;
        const side = ribMat(P.navy, 8), front = ribMat(P.navy, 7);
        const hb = new THREE.Mesh(gBox(hw, hh, hd), [side, side, mat(P.roofDark), mat(P.roofDark), front, front]);
        hb.position.set(hx, hh / 2, hz); hb.castShadow = hb.receiveShadow = true; g.add(hb);
        const roof = new THREE.Mesh(gable('gableGarage', hw + 0.8, 2.2, hd + 0.6), mat(P.roofDark));
        roof.position.set(hx, hh, hz); roof.castShadow = true; g.add(roof);
        box(hw + 0.1, 0.25, hd + 0.1, gold(), hx, hh - 0.3, hz, g, false);
        for (let k = 0; k < 2; k++) box(0.15, 4.4, 5.2, rollerMat, hx + hw / 2 + 0.02, 0, hz - 3.6 + k * 7.2, g, false);
        for (let k = 0; k < 2; k++) box(1.2, 0.1, 2, mat('#cfd8e2', { roughness: 0.3 }), hx - 3, hh + 1.2, hz - 4 + k * 8, g, false);
      }
      fence(-30, 24, 30, 24, g, [-6.3, 6.3]);
      if (!b.has('P2')) fence(-30, -22, 30, -22, g);
      fence(-30, back, -30, 24, g);
      fence(30, back, 30, 24, g);
    },
    P2(g) { // agrandissement x2
      plane(60, 62, P.asphaltLt, 0, 0.022, -53, g);
      laneMarks(g, -20, -56);
      SLOTS.filter((s) => s.req === 'P2').forEach((s) => slotMark(s, g));
      fence(-30, -83, 30, -83, g);
      for (const [x, z] of [[-29, -50], [29, -50], [-29, -80], [29, -80]]) streetLamp(x, z, g);
    },
    P3(g, b) { // entrepôt dédié, au fond du site, façade à quais tournée vers la cour
      const L = LOTS.P3;
      box(L.w, 1.2, L.d, mat('#bfbcb5'), L.x, 0, L.z, g);
      const side = ribMat(P.white, 9), front = ribMat(P.white, 17);
      const body = new THREE.Mesh(gBox(L.w, 10, L.d), [side, side, mat('#cfd1d4'), mat('#cfd1d4'), front, front]);
      body.position.set(L.x, 1.2 + 5, L.z); body.castShadow = body.receiveShadow = true; g.add(body);
      box(L.w + 0.1, 1.4, L.d + 0.1, mat(P.navy), L.x, 1.2, L.z, g);
      box(L.w + 0.14, 0.4, L.d + 0.14, gold(), L.x, 10.6, L.z, g, false);
      box(L.w + 0.3, 0.5, L.d + 0.3, mat('#e3e5e8'), L.x, 11.2, L.z, g);
      for (let k = 0; k < 5; k++) box(1.4, 0.25, L.d - 4, mat('#e8eef5', { roughness: 0.4 }), L.x - 13 + k * 6.5, 11.7, L.z, g, false);
      if (!b.has('P4')) { box(1.4, 2.4, 0.12, mat(P.navy), L.x + 6, 1.2, L.z + L.d / 2 + 0.05, g, false); box(5, 4.2, 0.12, rollerMat, L.x - 4, 1.2, L.z + L.d / 2 + 0.05, g, false); }
      const owned = b.has('P5');
      const sign = texMat(`sign${depot.name}${owned}`, () => new THREE.MeshStandardMaterial({
        map: textTex(768, 128, (c, w, h) => {
          c.fillStyle = HSTC.navy; c.fillRect(0, 0, w, h);
          c.fillStyle = HSTC.gold; c.font = '800 72px Inter, Arial, sans-serif'; c.textBaseline = 'middle'; c.fillText('HST', 26, h / 2 + 2);
          c.fillStyle = '#ffffff'; fit(c, (depot.name || '').toUpperCase(), w - 220, 36, 700);
          c.fillText((depot.name || '').toUpperCase(), 200, 46);
          const sub = (owned ? LX.owned : LX.rented) + (rival ? `  ·  ${LX.ex}${rival.name.toUpperCase()}` : '');
          c.fillStyle = owned ? '#7fd59b' : '#aab4c4'; fit(c, sub, w - 220, 26, 600);
          c.fillText(sub, 200, 92);
        }), roughness: 0.6,
      }));
      const s = new THREE.Mesh(geo('signP3', () => new THREE.PlaneGeometry(12, 2)), sign);
      s.position.set(L.x - 6, 9.1, L.z + L.d / 2 + 0.04); g.add(s);
    },
    P4(g) { // quais de chargement dédiés (4 portes)
      const z = LOTS.P3.z + LOTS.P3.d / 2;
      box(26, 0.35, 3.2, mat('#e3e5e8'), -6, 7.2, z + 1.5, g);
      box(26.1, 0.2, 0.1, gold(), -6, 7.2, z + 3.1, g, false);
      DOCK_X.forEach((x, k) => {
        box(4.6, 5, 0.25, mat('#3b4352'), x, 1.0, z + 0.05, g);
        const r = box(3.9, 4, 0.12, rollerMat, x, 1.2, z + 0.2, g, false);
        if (k % 2 === 0) { r.scale.y = 0.25; r.position.y = 1.2 + 4 - 0.5; box(3.8, 3, 0.05, mat('#1b2130'), x, 1.2, z + 0.16, g, false); }
        box(3.8, 1.2, 1.4, mat('#5b6068'), x, 0, z + 0.7, g);
        box(0.4, 0.6, 0.3, mat(P.tire), x - 2.1, 0.5, z + 1.45, g); box(0.4, 0.6, 0.3, mat(P.tire), x + 2.1, 0.5, z + 1.45, g);
        const lamp = mat(k % 2 ? '#e0463a' : '#3ccf7c', { emissive: k % 2 ? '#e0463a' : '#3ccf7c', emissiveIntensity: 0.8 });
        box(0.3, 0.3, 0.1, lamp, x + 2.5, 5.4, z + 0.2, g, false);
      });
    },
    P6(g) { // zone frigorifique accolée à l'entrepôt
      const L = LOTS.P6;
      const wall = texMat('frigoWall', () => new THREE.MeshStandardMaterial({ map: canvasTex(64, 64, (c, w, h) => { c.fillStyle = '#f4f6f8'; c.fillRect(0, 0, w, h); c.fillStyle = 'rgba(0,0,0,0.06)'; for (let y = 0; y < h; y += 16) c.fillRect(0, y, w, 2); }, [3, 3]), roughness: 0.5 }));
      box(L.w, 1.2, L.d, mat('#bfbcb5'), L.x, 0, L.z, g);
      box(L.w, 7.5, L.d, wall, L.x, 1.2, L.z, g);
      box(L.w + 0.1, 0.6, L.d + 0.1, mat('#3a7bd5'), L.x, 7.4, L.z, g, false);
      for (let k = 0; k < 2; k++) { box(2.6, 1.2, 1.8, mat('#d3d7dc'), L.x - 2.5 + k * 5, 8.7, L.z - 2, g); cyl(0.6, 0.12, mat('#2b2e33'), L.x - 2.5 + k * 5, 9.97, L.z - 2, g, 16); }
      const z = L.z + L.d / 2;
      box(4.2, 4.6, 0.2, mat('#3b4352'), L.x, 1.0, z + 0.05, g);
      box(3.6, 3.8, 0.12, mat('#e9eef3'), L.x, 1.2, z + 0.18, g, false);
      box(3.6, 1.2, 1.3, mat('#5b6068'), L.x, 0, z + 0.65, g);
      const t = texMat('frigoSign', () => new THREE.MeshStandardMaterial({ map: textTex(256, 64, (c, w, h) => { c.fillStyle = '#3a7bd5'; c.fillRect(0, 0, w, h); c.fillStyle = '#fff'; c.font = '700 34px Inter, Arial'; c.textAlign = 'center'; c.textBaseline = 'middle'; fit(c, LX.cold, w - 16, 34, 700); c.fillText(LX.cold, w / 2, h / 2 + 2); }) }));
      const p = new THREE.Mesh(geo('frigoP', () => new THREE.PlaneGeometry(5, 1.25)), t); p.position.set(L.x, 6.4, z + 0.04); g.add(p);
    },
    P7(g) { // quai renforcé BTP lourd : plateforme acier + rampe + portique
      const L = LOTS.P7;
      box(L.w, 1.3, L.d, mat('#8d9299', { metalness: 0.35, roughness: 0.5 }), L.x, 0, L.z, g);
      box(L.w, 0.22, 0.35, hazardMat, L.x, 1.3, L.z + L.d / 2 - 0.18, g, false);
      const ramp = box(L.w - 1, 0.25, 4.6, mat('#7e848b', { metalness: 0.3 }), L.x, 0.52, L.z + L.d / 2 + 2.2, g);
      ramp.rotation.x = 0.28;
      for (const dx of [-L.w / 2 + 0.3, L.w / 2 - 0.3]) box(0.45, 7.4, 0.45, mat(P.yellow), L.x + dx, 0, L.z + 1.5, g);
      box(L.w, 0.5, 0.5, mat(P.yellow), L.x, 7.2, L.z + 1.5, g);
      box(0.9, 0.5, 0.9, mat(P.chassis), L.x + 1, 6.7, L.z + 1.5, g);
      box(0.05, 3.4, 0.05, mat('#222'), L.x + 1, 3.3, L.z + 1.5, g, false);
    },
    P8(g) { // hangar haute hauteur T3+ (véhicules hors gabarit)
      const L = LOTS.P8;
      const side = ribMat(P.navy, 9), front = ribMat(P.navy, 8);
      const body = new THREE.Mesh(gBox(L.w, 9, L.d), [side, side, mat(P.roofDark), mat(P.roofDark), front, front]);
      body.position.set(L.x, 4.5, L.z); body.castShadow = body.receiveShadow = true; g.add(body);
      const roof = new THREE.Mesh(gable('gableP8', L.w + 0.8, 2.6, L.d + 0.6), mat(P.roofDark));
      roof.position.set(L.x, 9, L.z); roof.castShadow = true; g.add(roof);
      box(L.w + 0.1, 0.3, L.d + 0.1, gold(), L.x, 8.7, L.z, g, false);
      box(0.15, 7.4, 8, rollerMat, L.x + L.w / 2 + 0.02, 0, L.z - 3, g, false);
      box(0.12, 0.6, 8.6, hazardMat, L.x + L.w / 2 + 0.05, 7.4, L.z - 3, g, false);
      const t = texMat('t3', () => new THREE.MeshStandardMaterial({ map: textTex(128, 64, (c, w, h) => { c.fillStyle = P.gold; c.fillRect(0, 0, w, h); c.fillStyle = P.navy; c.font = '800 40px Inter, Arial'; c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText('T3+', w / 2, h / 2 + 2); }) }));
      const p = new THREE.Mesh(geo('t3P', () => new THREE.PlaneGeometry(2.4, 1.2)), t); p.position.set(L.x + L.w / 2 + 0.06, 8.4, L.z + 4.5); p.rotation.y = Math.PI / 2; g.add(p);
    },
    P9(g) { // ombrière solaire + bornes de recharge rapide
      const L = LOTS.P9;
      plane(L.w, L.d, '#3f6f4f', L.x, 0.03, L.z, g);
      for (let k = 0; k < 4; k++) plane(0.15, L.d - 1, P.line, L.x - 5.25 + k * 3.5, 0.04, L.z, g);
      for (const [dx, dz] of [[-5.5, -4.5], [5.5, -4.5], [-5.5, 4.5], [5.5, 4.5]]) box(0.3, 4.2, 0.3, mat('#d8dadd'), L.x + dx, 0, L.z + dz, g);
      const top = box(L.w + 1, 0.25, L.d + 1, solarMat, L.x, 4.2, L.z, g);
      top.rotation.x = 0.08;
      for (let k = 0; k < 3; k++) {
        box(0.6, 1.6, 0.4, mat('#e9ecef'), L.x - 3.5 + k * 3.5, 0, L.z - 5, g);
        box(0.5, 0.35, 0.05, mat('#46c37b', { emissive: '#46c37b', emissiveIntensity: 0.6 }), L.x - 3.5 + k * 3.5, 1.1, L.z - 4.78, g, false);
      }
    },
    P10(g) { // pompe diesel dédiée, à la sortie
      const L = LOTS.P10;
      plane(L.w, L.d, P.concrete, L.x, 0.03, L.z, g);
      for (const dx of [-3, 3]) box(0.3, 4.4, 0.3, mat('#e3e5e8'), L.x + dx, 0, L.z, g);
      box(L.w, 0.6, L.d - 1, mat('#f2f3f5'), L.x, 4.4, L.z, g);
      box(L.w + 0.1, 0.25, L.d - 0.9, mat(P.navy), L.x, 4.6, L.z, g, false);
      box(L.w + 0.12, 0.12, L.d - 0.88, gold(), L.x, 4.45, L.z, g, false);
      box(1.6, 0.2, 1, mat(P.concrete), L.x, 0, L.z, g);
      box(0.9, 1.7, 0.6, mat('#e9ecef'), L.x, 0.2, L.z, g);
      box(0.92, 0.3, 0.62, mat(P.navy), L.x, 1.6, L.z, g, false);
    },
    P11(g) { // atelier mécanique, portes sur l'allée
      const L = LOTS.P11;
      const wall = ribMat('#dcdde0', 10);
      const body = new THREE.Mesh(gBox(L.w, 6.5, L.d), [wall, wall, mat('#bfc3c8'), mat('#bfc3c8'), wall, wall]);
      body.position.set(L.x, 3.25, L.z); body.castShadow = body.receiveShadow = true; g.add(body);
      box(L.w + 0.3, 0.6, L.d + 0.3, mat(P.navy), L.x, 6.2, L.z, g);
      box(L.w + 0.32, 0.15, L.d + 0.32, gold(), L.x, 6.05, L.z, g, false);
      for (let k = 0; k < 2; k++) box(0.15, 4.8, 5.2, rollerMat, L.x - L.w / 2 - 0.08, 0, L.z - 3.6 + k * 7.2, g, false);
      for (let k = 0; k < 5; k++) cyl(0.55, 0.35, mat(P.tire), L.x + 3, 0.18 + k * 0.36, L.z - L.d / 2 - 1.2, g, 14);
      for (let k = 0; k < 3; k++) cyl(0.55, 0.35, mat(P.tire), L.x + 4.3, 0.18 + k * 0.36, L.z - L.d / 2 - 1.2, g, 14);
      const t = texMat('atelier', () => new THREE.MeshStandardMaterial({ map: textTex(256, 64, (c, w, h) => { c.fillStyle = P.navy; c.fillRect(0, 0, w, h); c.fillStyle = P.gold; c.font = '700 34px Inter, Arial'; c.textAlign = 'center'; c.textBaseline = 'middle'; fit(c, LX.workshop, w - 16, 34, 700); c.fillText(LX.workshop, w / 2, h / 2 + 2); }) }));
      const p = new THREE.Mesh(geo('atelierP', () => new THREE.PlaneGeometry(4, 1)), t); p.position.set(L.x - L.w / 2 - 0.1, 5.3, L.z); p.rotation.y = -Math.PI / 2; g.add(p);
    },
    P12(g) { // zone ADR sécurisée, isolée dans l'angle du site
      const L = LOTS.P12;
      plane(L.w, L.d, '#c7c3ba', L.x, 0.03, L.z, g);
      const om = mat(P.orange, { roughness: 0.6 });
      const panel = texMat('orangeMesh', () => new THREE.MeshStandardMaterial({ color: P.orange, transparent: true, opacity: 0.5, roughness: 0.6, side: THREE.DoubleSide }));
      const x0 = L.x - L.w / 2, x1 = L.x + L.w / 2, z0 = L.z - L.d / 2, z1 = L.z + L.d / 2;
      for (const [ax, az, bx, bz] of [[x0, z0, x1, z0], [x0, z1, x1 - 3.5, z1], [x0, z0, x0, z1], [x1, z0, x1, z1]]) {
        const len = Math.hypot(bx - ax, bz - az);
        box(Math.abs(bx - ax) || 0.05, 1.8, Math.abs(bz - az) || 0.05, panel, (ax + bx) / 2, 0.1, (az + bz) / 2, g, false);
        const n = Math.max(1, Math.round(len / 2.5));
        for (let k = 0; k <= n; k++) box(0.12, 2, 0.12, om, ax + (bx - ax) * k / n, 0, az + (bz - az) * k / n, g);
      }
      box(3.4, 2.6, 3, mat(P.whiteWarm), x1 - 2.2, 0, z0 + 2, g);
      box(3.6, 0.25, 3.2, mat(P.orange), x1 - 2.2, 2.6, z0 + 2, g);
      const dz = texMat('danger', () => new THREE.MeshStandardMaterial({ map: canvasTex(64, 64, (c, w) => { c.fillStyle = '#f2c21b'; c.beginPath(); c.moveTo(w / 2, 4); c.lineTo(w - 4, w - 6); c.lineTo(4, w - 6); c.fill(); c.fillStyle = '#111'; c.font = '700 34px Arial'; c.textAlign = 'center'; c.fillText('!', w / 2, w - 14); }), transparent: true }));
      for (let k = 0; k < 2; k++) { const d = new THREE.Mesh(geo('dz', () => new THREE.PlaneGeometry(0.8, 0.8)), dz); d.position.set(x0 + 1.5 + k * 3, 1.2, z1 + 0.06); g.add(d); }
    },
    P13(g) { // panneaux photovoltaïques sur le toit du QG
      for (let k = 0; k < 2; k++) { const p = box(4, 0.15, 7.5, solarMat, 20.4 + k * 4.6, 7.9, -4.6, g); p.rotation.z = 0.18; }
    },
    P14(g) { // cuve carburant 2 000 L
      const L = LOTS.P14;
      plane(L.w, L.d, P.concrete, L.x, 0.03, L.z, g);
      box(L.w - 0.6, 0.5, L.d - 0.8, mat('#b9b6ae'), L.x, 0, L.z, g);
      cyl(1.1, 4.2, mat('#eef0f2', { roughness: 0.4, metalness: 0.2 }), L.x, 1.9, L.z, g, 20, 'x');
      for (const dx of [-1.4, 1.4]) box(0.3, 0.9, 1.6, mat('#7e848b'), L.x + dx, 0.5, L.z, g);
      for (let k = 0; k < 4; k++) box(0.2, 1.1, 0.2, mat('#f2c21b'), L.x - 2.4 + k * 1.6, 0, L.z + 2.3, g);
    },
  };

  // ── panneaux d'entrée : investissements sans bâtiment ──
  // P5 (achat terrain + entrepôt) à gauche du portail, EXT (+100 km) à droite.
  // Disponible : panneau « à vendre » / panneau en attente ; construit : panneau définitif.
  const signFace = (id, state) => texMat(`sign-${id}-${state}`, () => new THREE.MeshStandardMaterial({
    map: textTex(640, 320, (c, w, h) => {
      const round = (x, y, ww, hh, r) => { c.beginPath(); c.roundRect(x, y, ww, hh, r); };
      c.textBaseline = 'middle';
      if (id === 'P5' && state === 'avail') {
        c.fillStyle = '#f7f5ef'; c.fillRect(0, 0, w, h);
        c.fillStyle = '#c4473a'; c.fillRect(0, 0, w, 92);
        c.textAlign = 'center';
        c.fillStyle = '#ffffff'; fit(c, LX.forSale, w - 40, 64); c.fillText(LX.forSale, w / 2, 50);
        c.fillStyle = HSTC.navy; fit(c, LX.landWarehouse, w - 40, 42, 700); c.fillText(LX.landWarehouse, w / 2, 148);
        c.fillStyle = '#5a6578'; fit(c, LX.endRent, w - 40, 30, 600); c.fillText(LX.endRent, w / 2, 200);
        c.fillStyle = HSTC.navy; round(150, 232, w - 300, 64, 14); c.fill();
        const price = new Intl.NumberFormat('fr-FR').format(nodeById('P5')?.cost ?? 120000).replace(/\u202f|\u00a0/g, ' ') + ' €';
        c.fillStyle = HSTC.gold; c.font = '800 40px Inter, Arial, sans-serif'; c.fillText(price, w / 2, 266);
        return;
      }
      c.fillStyle = HSTC.navy; c.fillRect(0, 0, w, h);
      c.strokeStyle = HSTC.gold; c.lineWidth = 10; c.strokeRect(10, 10, w - 20, h - 20);
      c.textAlign = 'left';
      if (id === 'P5') {
        c.fillStyle = HSTC.gold; c.beginPath(); c.arc(130, h / 2, 82, 0, Math.PI * 2); c.fill();
        c.fillStyle = HSTC.navy; c.font = '800 110px Inter, Arial, sans-serif'; c.textAlign = 'center'; c.fillText('€', 130, h / 2 + 8);
        c.textAlign = 'left';
        c.fillStyle = '#ffffff'; fit(c, LX.property, w - 270, 54); c.fillText(LX.property, 240, 112);
        c.fillStyle = HSTC.gold; c.font = '800 54px Inter, Arial, sans-serif'; c.fillText('HST', 240, 172);
        c.fillStyle = '#aab4c4'; fit(c, LX.acquired, w - 270, 28, 600); c.fillText(LX.acquired, 240, 232);
      } else {
        c.strokeStyle = 'rgba(255,255,255,0.55)'; c.lineWidth = 5;
        c.beginPath(); c.arc(130, h / 2, 78, 0, Math.PI * 2); c.stroke();
        c.beginPath(); c.ellipse(130, h / 2, 34, 78, 0, 0, Math.PI * 2); c.stroke();
        c.beginPath(); c.moveTo(52, h / 2); c.lineTo(208, h / 2); c.stroke();
        c.strokeStyle = HSTC.gold; c.lineWidth = 6; c.beginPath(); c.arc(130, h / 2, 104, -0.9, 0.9); c.stroke();
        c.fillStyle = '#aab4c4'; fit(c, LX.reach, w - 290, 30, 700); c.fillText(LX.reach, 260, 96);
        c.fillStyle = HSTC.gold; c.font = '800 92px Inter, Arial, sans-serif'; c.fillText('+100 km', 255, 170);
        c.fillStyle = '#ffffff'; fit(c, LX.extLog, w - 290, 28, 600); c.fillText(LX.extLog, 260, 240);
      }
    }), roughness: 0.55, transparent: state === 'ghost', opacity: state === 'ghost' ? 0.45 : 1, depthWrite: state !== 'ghost',
  }));
  const EMBLEMS = {
    P5: { x: -15.5, y: 3.6, z: 26.4 },
    EXT: { x: 15.5, y: 3.6, z: 26.4 },
  };
  let spinners = [];
  const buildEmblem = (id, g, available) => {
    const E = EMBLEMS[id];
    g.position.set(E.x, 0, E.z);
    const state = available ? (id === 'P5' ? 'avail' : 'ghost') : 'built';
    const post = mat('#8d949b', { metalness: 0.4, roughness: 0.5 });
    box(0.25, 5.6, 0.25, post, -2.9, 0, -0.15, g);
    box(0.25, 5.6, 0.25, post, 2.9, 0, -0.15, g);
    const back = mat(HSTC.navy);
    const face = signFace(id, state);
    const panel = new THREE.Mesh(gBox(6.6, 3.3, 0.18), [back, back, back, back, face, back]);
    panel.position.set(0, E.y, 0); panel.castShadow = state !== 'ghost'; panel.receiveShadow = true;
    if (state === 'ghost') panel.material = [face, face, face, face, face, face].map((m, i) => (i === 4 ? m : (() => { const c = back.clone(); c.transparent = true; c.opacity = 0.35; return c; })()));
    g.add(panel);
    if (state === 'built') box(6.8, 0.22, 0.4, mat(HSTC.gold, { roughness: 0.4, metalness: 0.3 }), 0, E.y + 1.65, 0, g, false);
    box(1.2, 0.3, 1.2, mat(P.concrete), -2.9, 0, -0.15, g, false);
    box(1.2, 0.3, 1.2, mat(P.concrete), 2.9, 0, -0.15, g, false);
    return g;
  };

  function rebuildSite() {
    const b = builtSet();
    untagTree(site);
    world.remove(site);
    walkers = []; forklifts = []; badges.length = 0;
    site = new THREE.Group();
    world.add(site);

    const isNew = (id) => prevBuilt && !prevBuilt.has(id);
    spinners = [];
    const order = ['P1', 'P2', 'P3', 'P4', 'P6', 'P7', 'P8', 'P9', 'P10', 'P11', 'P12', 'P13', 'P14', 'P5', 'EXT'];
    for (const id of order) {
      const L = LOTS[id];
      const E = EMBLEMS[id];
      if (b.has(id)) {
        const g = new THREE.Group();
        site.add(g);
        if (id !== 'P1') tagPick(g, { type: 'zone', id });
        // zones reprises au rachat : bâtiments aux couleurs du concurrent racheté
        const branded = rival && (id === 'P1' || acquiredSet.has(id));
        if (branded) { P.navy = rival.color; P.gold = rival.accent; }
        if (E) buildEmblem(id, g, false); else builders[id](g, b);
        if (branded) { P.navy = PAL.navy; P.gold = PAL.gold; }
        if (isNew(id)) {
          if (E) { g.scale.y = 0.01; growing.push({ g, t: 0 }); addDust(E.x, E.z, 8); }
          else { g.scale.y = 0.01; growing.push({ g, t: 0 }); if (L) addDust(L.x, L.z, Math.max(L.w, L.d) * 0.6); }
        }
      } else if (E) {
        if (!isAvailable(id, b)) continue;
        const g = new THREE.Group(); site.add(g);
        tagPick(g, { type: 'zone', id });
        buildEmblem(id, g, true);
        addBadge(g, 0, E.y + 2.6, 0, UI.available, '#f0b43c', '#1b1405', { type: 'zone', id });
      } else if (L) {
        const avail = isAvailable(id, b);
        const owned = (L.z - L.d / 2 >= -22) || b.has('P2');
        if (!avail && !owned) continue;
        const g = new THREE.Group(); site.add(g);
        tagPick(g, { type: 'zone', id });
        lotOutline(L, avail ? '#f0b43c' : '#d5dae1', g, avail ? 0.95 : 0.5);
        if (avail) addBadge(g, L.x, id === 'P13' ? 9 : 3, L.z, UI.available, '#f0b43c', '#1b1405', { type: 'zone', id });
      }
    }
    if (!b.has('P2')) plane(60, 61, P.grassGreen, 0, 0.012, -52.5, site);

    // marchandises des contrats actifs
    const count = {};
    (depot.contracts || []).forEach((c) => {
      const spot = goodsSpot(c.goods, b);
      if (!spot || !goods[c.goods]) return;
      const k = count[c.goods] || 0;
      count[c.goods] = k + 1;
      const n = Math.max(1, Math.min(9, Math.round(c.volume || 4)));
      const cg = new THREE.Group(); site.add(cg);
      tagPick(cg, { type: 'contract', id: c.id });
      const shift = { sec: [k * 6.6, 0], frigo: [0, k * 2.2], btp: [0, k * 2.4], adr: [0, -k * 5.6], vrac: [0, 0] }[c.goods] || [0, 0];
      goods[c.goods](spot[0] + shift[0], spot[1] + shift[1], n, cg);
    });

    // personnel (d'après les effectifs RH du dépôt)
    const st = depot.staff || {};
    const ppl = new THREE.Group(); site.add(ppl);
    tagPick(ppl, { type: 'qg', id: 'QG' });
    for (let i = 0; i < Math.min(3, st.exploitants || 0); i++) person(14.6 + i * 1.3, 3 + (i % 2), ppl, '#3a7bd5');
    if (b.has('P11')) for (let i = 0; i < Math.min(3, st.mecaniciens || 0); i++) person(13.8, -45 + i * 2.2, ppl, P.navy);
    for (let i = 0; i < Math.min(2, st.agents || 0); i++) person(6.8 + i * 1.1, 19.6, ppl);
    const free = Math.min(3, Math.max(0, (st.chauffeurs || 0) - (depot.vehicles || []).filter((v) => v.status === 'mission').length));
    for (let i = 0; i < free; i++) person(17.6 + i * 1.2, 0.6, ppl, '#f2c21b');
    if (b.has('P3')) {
      const doors = b.has('P4') ? [-9, -3, 3] : [-4];
      for (let i = 0; i < Math.min(3, st.caristes || 0); i++) {
        const f = makeForklift(site);
        const x = doors[i % doors.length] + (i >= doors.length ? 1.5 : 0);
        forklifts.push({ obj: f, a: [x, -60.6], b: [x + 0.6, -55.6 + (i % 2) * 1.5], t: i * 0.4, dir: 1, pause: 0, loaded: i % 2 === 0 });
      }
    }
    prevBuilt = b;
  }

  function addDust(x, z, size) {
    const m = new THREE.MeshBasicMaterial({ color: '#e2dbc8', transparent: true, opacity: 0.7, depthWrite: false });
    const r = new THREE.Mesh(geo('dust', () => new THREE.RingGeometry(0.55, 1, 48).rotateX(-Math.PI / 2)), m);
    r.position.set(x, 0.25, z);
    world.add(r);
    dusts.push({ r, t: 0, size });
  }

  // ───────────────────────── Véhicules de la flotte ─────────────────────────
  const sideMat = (brand, body) => texMat(`side${brand}${body}${depot.name}`, () => {
    const col = BRAND_COLORS[brand] || '#2f63d6';
    return new THREE.MeshStandardMaterial({
      map: textTex(1024, 256, (g, w, h) => {
        g.fillStyle = body === 'tautliner' ? '#e2e4e7' : '#f4f6f8'; g.fillRect(0, 0, w, h);
        if (body === 'tautliner') { g.fillStyle = 'rgba(0,0,0,0.05)'; for (let x = 0; x < w; x += 46) g.fillRect(x, 0, 3, h); }
        g.fillStyle = '#1b2333'; g.fillRect(0, h - 40, w, 40);
        g.fillStyle = '#f0b43c'; g.fillRect(0, h - 48, w, 8);
        g.fillStyle = '#1b2333'; g.font = '800 110px Inter, Arial, sans-serif'; g.textBaseline = 'middle';
        g.fillText('HST', 50, 100);
        g.fillStyle = col; g.font = '700 46px Inter, Arial, sans-serif';
        g.fillText(body === 'frigo' ? LX.fridge : LX.transports, 300, 96);
        g.fillStyle = '#5a6578'; g.font = '600 34px Inter, Arial, sans-serif';
        g.fillText((depot.name || '').toUpperCase(), 300, 150);
      }), roughness: 0.65,
    });
  });
  const wheels = (g, zs, r = 0.52, x = 1.08) => zs.forEach((z) => [-1, 1].forEach((s) => cyl(r, 0.42, mat(P.tire), s * x, r, z, g, 14, 'x')));
  const cab = (g, col, z0, big) => {
    const cm = mat(col, { roughness: 0.45, metalness: 0.1 });
    const h = big ? 2.9 : 2.4;
    box(2.5, h, 2.6, cm, 0, 0.9, z0 + 1.3, g);
    if (big) box(2.4, 0.7, 1.6, cm, 0, 0.9 + h, z0 + 0.9, g);
    box(2.2, 1.0, 0.08, glassMat, 0, 0.9 + h - 1.45, z0 + 2.62, g, false);
    box(2.53, 0.8, 0.9, glassMat, 0, 0.9 + h - 1.35, z0 + 2.0, g, false);
    box(2.55, 0.42, 0.3, mat(P.chassis), 0, 0.5, z0 + 2.65, g);
    const lm = mat('#fff7d6', { emissive: '#fff2c0', emissiveIntensity: 0.5 });
    box(0.45, 0.2, 0.06, lm, -0.85, 1.1, z0 + 2.66, g, false); box(0.45, 0.2, 0.06, lm, 0.85, 1.1, z0 + 2.66, g, false);
  };

  // modèle 3D d'après le type et la carrosserie du véhicule de la flotte
  const makeVehicle = (v) => {
    const g = new THREE.Group();
    const col = v.color || BRAND_COLORS[v.brand] || '#2f63d6';
    const type = v.type || 'semi';
    const body = v.body || (type === 'vul' ? 'fourgon' : 'tautliner');
    if (type === 'vul') {
      box(2.1, 2.4, 5.8, mat('#f2f3f5', { roughness: 0.5 }), 0, 0.45, 0, g);
      box(2.12, 0.5, 5.82, mat(col), 0, 0.5, 0, g, false);
      box(1.9, 0.8, 0.06, glassMat, 0, 1.9, 2.92, g, false);
      wheels(g, [-1.8, 1.9], 0.42, 0.95);
      g.userData.len = 6;
      return g;
    }
    const cargo = (len, h) => {
      const side = sideMat(v.brand, body);
      const m = new THREE.Mesh(gBox(2.5, h, len), [side, side, mat('#f2f3f5'), mat('#f2f3f5'), mat('#f2f3f5'), mat('#e0e3e7')]);
      m.castShadow = m.receiveShadow = true;
      return m;
    };
    if (type === 'porteur') {
      box(1.9, 0.4, 8.6, mat(P.chassis), 0, 0.55, 0, g);
      if (body === 'benne') { box(2.5, 1.6, 5.6, mat(col, { roughness: 0.5 }), 0, 1.0, -1.3, g); box(2.2, 0.4, 5.3, mat('#9b8a6c', { flatShading: true }), 0, 2.5, -1.3, g, false); }
      else { const c = cargo(5.8, 2.7); c.position.set(0, 1.0 + 1.35, -1.3); g.add(c); }
      if (body === 'frigo') box(1.9, 0.9, 0.5, mat('#2b2e33'), 0, 2.6, 1.75, g);
      cab(g, col, 1.75, false);
      wheels(g, [-2.6, 3.1]);
      g.userData.len = 9;
      return g;
    }
    box(1.9, 0.45, 14.4, mat(P.chassis), 0, 0.6, 0.6, g);
    wheels(g, [-5.8, -4.5, -3.2, 5.6, 7.5]);
    cab(g, col, 5.9, true);
    if (body === 'citerne') {
      cyl(1.2, 11.5, mat('#d7dbe0', { roughness: 0.25, metalness: 0.6 }), 0, 2.45, -1.1, g, 22, 'z');
      for (let k = 0; k < 3; k++) cyl(0.35, 0.3, mat('#a9afb6', { metalness: 0.5 }), 0, 3.75, -5 + k * 4, g, 12);
      box(0.06, 0.45, 9, mat('#f0b43c'), 1.21, 2.0, -1.1, g, false);
      box(0.06, 0.45, 9, mat('#f0b43c'), -1.21, 2.0, -1.1, g, false);
    } else if (body === 'plateau') {
      box(2.5, 0.3, 12, mat('#5a6068'), 0, 1.15, -1, g);
      for (let k = 0; k < 3; k++) box(2.2, 0.9, 2.6, mat('#9aa0a7', { metalness: 0.4 }), 0, 1.45, -5 + k * 4, g);
      for (let k = 0; k < 6; k++) box(0.15, 1.1, 0.15, mat('#2b2e33'), k % 2 ? 1.2 : -1.2, 1.45, -6.5 + Math.floor(k / 2) * 5.5, g);
    } else if (body === 'benne') {
      box(2.5, 0.25, 9.5, mat('#4f545b'), 0, 1.15, -2, g);
      box(2.5, 1.9, 9.5, mat(col, { roughness: 0.5 }), 0, 1.4, -2, g);
      box(2.2, 0.4, 9.2, mat('#9b8a6c', { flatShading: true }), 0, 3.2, -2, g, false);
    } else {
      const c = cargo(12, 3.1); c.position.set(0, 1.15 + 1.55, -1); g.add(c);
      if (body === 'frigo') { box(2.1, 1.3, 0.6, mat('#2b2e33'), 0, 3.0, 5.3, g); box(1.8, 0.9, 0.05, mat('#55595f'), 0, 3.2, 5.62, g, false); }
    }
    g.userData.len = 15.6;
    return g;
  };

  const vehicles = new Map();
  const curve = (pts) => {
    const c = new THREE.CatmullRomCurve3(pts.map(([x, z]) => new THREE.Vector3(x, 0, z)), false, 'centripetal');
    c.len = c.getLength();
    return c;
  };
  const slotYaw = (s) => (s.rot ? -Math.PI / 2 : 0);
  const EXIT = [[0, 21], [0, 27], [-6, 30.8], [-40, 31], [-200, 31]];
  const ROAD_IN = [[-200, 35], [-25, 35], [-5, 33], [0, 27], [0, 21]];
  const departLegs = (s) => {
    if (s.rot) return [{ c: curve([[s.x, s.z], [s.x - 7, s.z + 1], [0, s.z + 10], [0, s.z + 24], ...EXIT]), speed: 9 }];
    const pts = [[s.x, s.z], [s.x, s.z + 6]];
    if (s.z + 14 < 19) pts.push([s.x * 0.25, s.z + 14]);
    return [{ c: curve([...pts, ...EXIT]), speed: 9 }];
  };
  const arriveLegs = (s) => {
    if (s.rot) return [{ c: curve([...ROAD_IN, [0, s.z + 24], [0, s.z + 10], [s.x - 7, s.z + 1], [s.x, s.z]]), speed: 8, ease: true }];
    const sign = Math.sign(s.x);
    const bz = Math.min(s.z + 10.5, 19);
    const fwd = [...ROAD_IN];
    if (s.z + 16 < 21) fwd.push([0, s.z + 16]);
    fwd.push([-sign * 4.5, bz]);
    return [
      { c: curve(fwd), speed: 9 },
      { c: curve([[-sign * 4.5, bz], [sign * 2, bz - 1.5], [s.x, s.z + 4], [s.x, s.z]]), speed: 3.2, reverse: true, ease: true },
    ];
  };
  const placeOnLeg = (v, dt, snap) => {
    const leg = v.legs[v.li];
    const u = Math.min(1, v.dist / leg.c.len);
    const p = leg.c.getPointAt(u), tan = leg.c.getTangentAt(u);
    if (leg.reverse) tan.negate();
    v.obj.position.copy(p);
    const target = Math.atan2(tan.x, tan.z);
    if (snap) v.yaw = target;
    else { let d = target - v.yaw; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2; v.yaw += d * (1 - Math.exp(-dt * 9)); }
    v.obj.rotation.y = v.yaw;
    return u;
  };
  const availableSlots = () => { const b = builtSet(); return SLOTS.filter((s) => !s.req || b.has(s.req)); };

  function syncVehicles(initial) {
    const list = depot.vehicles || [];
    const ids = new Set(list.map((v) => v.id));
    for (const [id, v] of vehicles) if (!ids.has(id)) { untagTree(v.obj); world.remove(v.obj); vehicles.delete(id); }
    const slots = availableSlots();
    const taken = new Set();
    for (const v of vehicles.values()) if (v.slot) taken.add(v.slot);
    list.forEach((spec) => {
      let v = vehicles.get(spec.id);
      const onSite = spec.status !== 'mission';
      if (!v) {
        const obj = makeVehicle(spec);
        tagPick(obj, { type: 'vehicle', id: spec.id });
        v = { spec, obj, state: 'away', yaw: 0, slot: null };
        vehicles.set(spec.id, v);
      }
      v.spec = spec;
      if (onSite && (v.state === 'away' || v.state === 'leaving')) {
        const slot = slots.find((s) => !taken.has(s));
        if (!slot) { v.overflow = true; return; }
        v.overflow = false;
        taken.add(slot);
        v.slot = slot;
        world.add(v.obj);
        if (initial || v.state === 'leaving') {
          v.obj.position.set(slot.x, 0, slot.z); v.yaw = slotYaw(slot); v.obj.rotation.y = v.yaw; v.state = 'parked';
        } else {
          v.legs = arriveLegs(slot); v.li = 0; v.dist = 0; v.state = 'arriving';
          placeOnLeg(v, 0, true);
        }
      } else if (!onSite && (v.state === 'parked' || v.state === 'arriving')) {
        taken.delete(v.slot);
        if (initial || v.state === 'arriving') { world.remove(v.obj); v.state = 'away'; v.slot = null; return; }
        v.legs = departLegs(v.slot); v.li = 0; v.dist = 0; v.state = 'leaving';
        v.slot = null;
      }
    });
  }

  const stepVehicle = (v, dt) => {
    if (v.state !== 'arriving' && v.state !== 'leaving') return;
    const leg = v.legs[v.li];
    const u0 = Math.min(1, v.dist / leg.c.len);
    const k = leg.ease ? 0.3 + 0.7 * Math.sin(Math.PI * Math.min(0.999, Math.max(0.001, u0))) : 1;
    v.dist += leg.speed * dt * k;
    if (placeOnLeg(v, dt) >= 1) {
      v.li++; v.dist = 0;
      if (v.li >= v.legs.length) {
        if (v.state === 'leaving') { world.remove(v.obj); v.state = 'away'; }
        else { v.state = 'parked'; v.yaw = slotYaw(v.slot); v.obj.rotation.y = v.yaw; v.obj.position.set(v.slot.x, 0, v.slot.z); }
      }
    }
  };

  // ───────────────────────── Caméra (vue drone) ─────────────────────────
  const camera = new THREE.PerspectiveCamera(32, 1, 1, 1500);
  const HOME = { target: new THREE.Vector3(-3 * MX, 0, -28), az: 0.12 * MX, el: T.camEl || 0.62, dist: T.camDist || 158 };
  const view = { target: HOME.target.clone(), az: HOME.az - 0.3, el: HOME.el + 0.12, dist: HOME.dist * 1.25 };
  const goal = { target: HOME.target.clone(), az: HOME.az, el: HOME.el, dist: HOME.dist };
  let follow = null;
  const applyCamera = () => {
    const ce = Math.cos(view.el);
    camera.position.set(
      view.target.x + Math.sin(view.az) * ce * view.dist,
      view.target.y + Math.sin(view.el) * view.dist,
      view.target.z + Math.cos(view.az) * ce * view.dist,
    );
    camera.lookAt(view.target);
    sun.position.set(view.target.x - 70, 120, view.target.z + 60);
    sun.target.position.copy(view.target);
  };
  const resize = () => {
    const w = container.clientWidth || 1, h = container.clientHeight || 1;
    renderer.setSize(w, h, false);
    el.style.width = w + 'px'; el.style.height = h + 'px';
    camera.aspect = w / h; camera.updateProjectionMatrix();
    applyCamera();
  };
  const ro = new ResizeObserver(resize);
  ro.observe(container);

  // ───────────────────────── Interaction ─────────────────────────
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  const pointers = new Map();
  let drag = null, pinch = 0;
  const pickAt = (cx, cy) => {
    const r = el.getBoundingClientRect();
    ndc.set(((cx - r.left) / r.width) * 2 - 1, -((cy - r.top) / r.height) * 2 + 1);
    raycaster.setFromCamera(ndc, camera);
    const hits = raycaster.intersectObjects(pickables, true);
    hits.sort((a, b) => (b.object.isSprite ? 1 : 0) - (a.object.isSprite ? 1 : 0) || a.distance - b.distance);
    for (const h of hits) { let o = h.object; while (o) { if (o.userData.pick) return o.userData.pick; o = o.parent; } }
    return null;
  };
  const onDown = (e) => {
    el.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) { const [a, b] = [...pointers.values()]; pinch = Math.hypot(a.x - b.x, a.y - b.y); drag = null; return; }
    drag = { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, rotate: e.button === 2 || e.shiftKey, moved: false };
  };
  const onMove = (e) => {
    if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()]; const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch > 0) { goal.dist = Math.max(45, Math.min(330, goal.dist * (pinch / d))); view.dist = goal.dist; }
      pinch = d; return;
    }
    if (!drag) { el.style.cursor = pickAt(e.clientX, e.clientY) ? 'pointer' : 'grab'; return; }
    const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
    drag.x = e.clientX; drag.y = e.clientY;
    if (Math.hypot(e.clientX - drag.sx, e.clientY - drag.sy) > 5) drag.moved = true;
    if (!drag.moved) return;
    el.style.cursor = 'grabbing';
    if (drag.rotate) {
      goal.az -= dx * 0.005; view.az = goal.az;
      goal.el = Math.max(0.25, Math.min(1.25, goal.el + dy * 0.004)); view.el = goal.el;
      return;
    }
    follow = null;
    const upp = (2 * view.dist * Math.tan((camera.fov * Math.PI) / 360)) / (el.clientHeight || 1);
    const right = new THREE.Vector3(Math.cos(view.az), 0, -Math.sin(view.az));
    const fwd = new THREE.Vector3(-Math.sin(view.az), 0, -Math.cos(view.az));
    goal.target.addScaledVector(right, -dx * upp).addScaledVector(fwd, (dy * upp) / Math.sin(view.el));
    goal.target.x = Math.max(-90, Math.min(90, goal.target.x));
    goal.target.z = Math.max(-110, Math.min(50, goal.target.z));
    view.target.copy(goal.target);
  };
  const onUp = (e) => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinch = 0;
    if (drag && !drag.moved && e.button !== 2) setSelection(pickAt(e.clientX, e.clientY), true);
    drag = null; el.style.cursor = 'grab';
  };
  const onWheel = (e) => { e.preventDefault(); goal.dist = Math.max(45, Math.min(330, goal.dist * Math.exp(e.deltaY * 0.0012))); };
  const onCtx = (e) => e.preventDefault();
  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);
  el.addEventListener('wheel', onWheel, { passive: false });
  el.addEventListener('contextmenu', onCtx);
  el.style.cursor = 'grab';

  // ───────────────────────── Sélection ─────────────────────────
  let selection = null;
  const ring = new THREE.Group();
  {
    const r = new THREE.Mesh(new THREE.RingGeometry(0.93, 1, 64).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: '#f0b43c', transparent: true, opacity: 0.95, depthWrite: false }));
    const d = new THREE.Mesh(new THREE.CircleGeometry(0.93, 64).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: '#f0b43c', transparent: true, opacity: 0.14, depthWrite: false }));
    r.position.y = 0.08; d.position.y = 0.075; ring.add(r, d); ring.visible = false; world.add(ring);
  }
  function setSelection(sel, notify) {
    selection = sel;
    follow = null;
    if (sel?.type === 'vehicle') {
      const v = vehicles.get(sel.id);
      if (v && v.state !== 'away') { follow = v.obj; goal.dist = 90; }
    } else if (sel?.type === 'zone' && (LOTS[sel.id] || EMBLEMS[sel.id])) {
      const L = LOTS[sel.id] || { ...EMBLEMS[sel.id], w: 20, d: 20 }; goal.target.set(L.x * MX, 0, L.z); goal.dist = Math.max(75, Math.min(150, Math.max(L.w, L.d) * 3));
    } else if (sel?.type === 'qg') { goal.target.set(18 * MX, 0, -2); goal.dist = 85; }
    if (notify) onSelect(sel);
  }

  // ───────────────────────── Boucle ─────────────────────────
  let raf = 0, last = performance.now(), tAcc = 0;
  const tmp = new THREE.Vector3();
  const tick = (now) => {
    raf = requestAnimationFrame(tick);
    const dt = Math.min(0.1, (now - last) / 1000);
    last = now; tAcc += dt;

    traffic.forEach((v) => {
      if (v.road) { v.x += v.speed * dt; if (v.x > 220) v.x = -220; v.obj.position.set(v.x, 0, v.z); return; }
      v.z += v.dir * v.speed * dt; if (v.z > 400) v.z = -400; if (v.z < -400) v.z = 400;
      v.obj.position.set(v.x, 0, v.z);
    });
    vehicles.forEach((v) => stepVehicle(v, dt));
    forklifts.forEach((f) => {
      const [ax, az] = f.a, [bx, bz] = f.b, len = Math.hypot(bx - ax, bz - az);
      if (f.pause > 0) { f.pause -= dt; if (f.pause <= 0) f.loaded = !f.loaded; }
      else {
        f.t += (dt * 2.6 / len) * f.dir;
        if (f.t >= 1) { f.t = 1; f.dir = -1; f.pause = 1.8; }
        if (f.t <= 0) { f.t = 0; f.dir = 1; f.pause = 1.8; }
      }
      f.obj.userData.load.visible = f.loaded;
      f.obj.userData.carriage.position.y = 0.15 + (f.pause > 0 ? Math.sin(Math.PI * (1 - f.pause / 1.8)) * 1.2 : 0);
      f.obj.position.set(ax + (bx - ax) * f.t, 0, az + (bz - az) * f.t);
      f.obj.rotation.y = Math.atan2(bx - ax, bz - az);
    });
    walkers.forEach((w) => { w.position.y = Math.abs(Math.sin(tAcc * 2 + w.userData.bob)) * 0.04; });
    for (let i = growing.length - 1; i >= 0; i--) {
      const gr = growing[i]; gr.t += dt / 1.3;
      const sc = Math.max(0.01, easeOutBack(Math.min(1, gr.t)));
      if (gr.all) gr.g.scale.setScalar(sc); else gr.g.scale.y = sc;
      if (gr.t >= 1) { if (gr.all) gr.g.scale.setScalar(1); else gr.g.scale.y = 1; growing.splice(i, 1); }
    }
    for (let i = dusts.length - 1; i >= 0; i--) {
      const d = dusts[i]; d.t += dt / 1.6;
      const s = d.size * (0.6 + d.t * 0.9);
      d.r.scale.set(s, 1, s); d.r.material.opacity = 0.7 * (1 - d.t);
      if (d.t >= 1) { world.remove(d.r); d.r.material.dispose(); dusts.splice(i, 1); }
    }
    spinners.forEach((sp) => { sp.obj.rotation.y = tAcc * 0.9 + sp.phase; sp.obj.position.y = sp.baseY + Math.sin(tAcc * 1.6 + sp.phase) * 0.5; });
    const bs = view.dist / 70;
    badges.forEach((b) => { b.scale.set(bs * 4.0, bs * 1.75, 1); b.position.y = b.userData.baseY + Math.sin(tAcc * 2.4 + b.userData.phase) * 0.3; });

    ring.visible = false;
    if (selection?.type === 'vehicle') {
      const v = vehicles.get(selection.id);
      if (v && v.state !== 'away') {
        ring.visible = true; tmp.copy(v.obj.position);
        const len = v.obj.userData.len || 15;
        ring.position.set(tmp.x, 0, tmp.z); ring.rotation.y = v.obj.rotation.y;
        const p = 1 + Math.sin(tAcc * 4) * 0.04;
        ring.scale.set(2.6 * p, 1, (len / 2 + 1.5) * p);
      }
    }

    if (follow) { follow.getWorldPosition(tmp); goal.target.set(tmp.x, 0, tmp.z); }
    const k = 1 - Math.exp(-dt * 4);
    view.target.lerp(goal.target, k);
    view.dist += (goal.dist - view.dist) * k;
    view.az += (goal.az - view.az) * k;
    view.el += (goal.el - view.el) * k;
    applyCamera();
    renderer.render(scene, camera);
  };

  rebuildSite();
  syncVehicles(true);
  resize();
  raf = requestAnimationFrame(tick);

  return {
    update(nextDepot, nextTree) {
      const keyOf = (d) => JSON.stringify([d.built, d.contracts, d.staff, d.name, (d.vehicles || []).filter((v) => v.status === 'mission').length]);
      const prevKey = keyOf(depot);
      depot = nextDepot; if (nextTree) tree = nextTree;
      if (keyOf(depot) !== prevKey) rebuildSite();
      syncVehicles(false);
    },
    select: (sel) => setSelection(sel, false),
    rotate: (dir) => { goal.az += (dir * Math.PI) / 6; },
    zoom: (f) => { goal.dist = Math.max(45, Math.min(330, goal.dist * f)); },
    home: () => { follow = null; selection = null; goal.target.copy(HOME.target); goal.dist = HOME.dist; goal.az = HOME.az; goal.el = HOME.el; },
    vehicleState: (id) => vehicles.get(id)?.state || 'away',
    slotsCount: () => availableSlots().length,
    dispose() {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('contextmenu', onCtx);
      geos.forEach((g) => g.dispose());
      mats.forEach((m) => m.dispose());
      texMats.forEach((m) => m.dispose());
      textures.forEach((t) => t.dispose());
      renderer.dispose();
      el.remove();
    },
  };
}
